import debounce from 'lodash.debounce';
import React, {
  memo,
  useEffect,
  useState,
  useRef,
  useMemo,
  Fragment
} from 'react';
import styled from 'styled-components';
import { ReactComponent as Plus } from '../images/plus.svg';
import { ReactComponent as NavUp } from '../images/nav-up-icon.svg';
import { ReactComponent as NavDown } from '../images/nav-down-icon.svg';
import HakerDaoLogo from 'bakerdao/images/category.png';
import { Flex, Text, Box } from '@makerdao/ui-components-core';
import RatioDisplay from './RatioDisplay';
import { Link, useNavigation } from 'react-navi';
import useModal from 'hooks/useModal';
import useMaker from 'hooks/useMaker';
import useAnalytics from 'hooks/useAnalytics';
import { getMeasurement } from '../styles/theme';
import lang from 'languages';
import useVaults from 'hooks/useVaults';
import { watch } from 'hooks/useObservable';
import useCheckRoute from 'hooks/useCheckRoute';
import { ETHToHT } from 'utils/ui';


// baker样式
import 'bakerdao/css/pc/left/btn-list.css'
import 'bakerdao/css/pc/left/add.css'

import 'bakerdao/css/mobile/cdp-list.css'

import './CDPCreateScreens/css/mobile.css'
import './CDPCreateScreens/css/borrow.css'

const NavbarItemContent = ({ children, ...props }) => (
  <Flex
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    borderRadius="default"
    {...props}
  >
    {children}
  </Flex>
);

const NavbarItem = ({ href, active, trackBtnClick, ...props }) => {
  return (
    <Link
      href={href}
      active={active}
      prefetch={true}
      onClick={trackBtnClick}
      css={`
        display: block;
        margin: 0 auto;
      `}
      {...props}
    >
      <NavbarItemContent {...props} />
    </Link>
  );
};

// 加号按钮
const AddCdpButton = ({ account, show, mobile }) => {
  const { trackBtnClick } = useAnalytics('NavBar');
  return (
    <Flex
      onClick={() => {
        trackBtnClick('CreateNew');
        account &&
          show({ modalType: 'cdpcreate', modalTemplate: 'fullscreen' });
      }}
      width={mobile && `${getMeasurement('navbarItemWidth')}px`}
      mx={mobile && '7px'}
      mt={mobile && '15px'}
      justifyContent="center"
      borderRadius="4px"
      py="s"
      css={`
        cursor: pointer;
      `}
    >
      <div
        // baker样式
        className="baker-left-add"
        style={{
          borderRadius: '2px',
          // backgroundColor: '#5a52c1',
          // 添加按钮背景色
          backgroundColor: '#f7b500',
          fontSize: '12px',
          color: '#fff',
          padding: '4px 15px',
          marginTop: '20px'
        }}
      >
        {lang.add_cdp}
      </div>
      {/* <Plus /> */}
    </Flex>
  );
};

const DirectionalButton = styled(NavbarItemContent)`
  height: 35px;
  cursor: pointer;
  visibility: ${props => (props.show ? 'visible' : 'hidden')};
`;

// const CdpContainer = styled(Flex)`
//   cursor: pointer;
//   flex-direction: ${props => (props.mobile ? 'row' : 'column')};
//   flex-wrap: ${props => (props.mobile ? 'wrap' : undefined)};
//   overflow: auto;
//   height: ${props => props.cdpsLength >= 4 && !props.mobile && '275px'};
//   ::-webkit-scrollbar {
//     width: 0px;
//   }
//   scrollbar-width: none;
// `;

const CdpContainer = styled(Flex)`
  cursor: pointer;
  flex-direction: ${props => (props.mobile ? 'row' : 'column')};
  flex-wrap: ${props => (props.mobile ? 'wrap' : undefined)};
  overflow: auto;
  ::-webkit-scrollbar {
    width: 0px;
  }
  scrollbar-width: none;
`;

const CDPList = memo(function({
  currentPath,
  viewedAddress,
  currentQuery,
  mobile
}) {
  const navigation = useNavigation();
  const { isSave } = useCheckRoute();
  const [listOpen, setListOpen] = useState(false);
  const { maker, account } = useMaker();
  const { userVaults } = useVaults();
  const userActiveVaults =
    userVaults &&
    userVaults.filter(
      vault => vault.collateralAmount && vault.collateralAmount.gt(0)
    );
  const [overviewPath, setOverviewPath] = useState(currentPath);
  const { trackBtnClick } = useAnalytics('NavBar');
  const active = currentPath === overviewPath;
  const emergencyShutdownActive = watch.emergencyShutdownActive();

  useMemo(() => {
    if (isSave) {
      setListOpen(false);
    } else if (account || viewedAddress) {
      setListOpen(true);
    }
  }, [account, isSave, viewedAddress]);

  useEffect(() => {
    if (account) {
      setOverviewPath(`${navigation.basename}/owner/${account.address}`);
    } else if (viewedAddress) {
      setOverviewPath(`${navigation.basename}/owner/${viewedAddress}`);
    }
  }, [maker, account, viewedAddress, setOverviewPath, navigation.basename]);

  const [scrollPosition, setScrollPosition] = useState({
    scrollTop: 0,
    maxScrollTop: 1
  });
  const { scrollTop, maxScrollTop } = scrollPosition;

  const cdpContainerRef = useRef(null);

  const handleOnScroll = () => {
    const maxScrollTop =
      cdpContainerRef.current.scrollHeight -
      cdpContainerRef.current.clientHeight;

    setScrollPosition({
      ...scrollPosition,
      scrollTop: cdpContainerRef.current.scrollTop,
      maxScrollTop
    });
  };

  const debounced = debounce(handleOnScroll, 100);

  const onDirectionalClick = direction => {
    const scrollAmount =
      userActiveVaults &&
      cdpContainerRef.current.scrollHeight / userActiveVaults.length;
    const maxScrollTop =
      cdpContainerRef.current.scrollHeight -
      cdpContainerRef.current.clientHeight;

    setScrollPosition({
      ...scrollPosition,
      scrollAmount
    });
    let newPosition =
      direction === 'up' && scrollAmount
        ? scrollTop - scrollAmount
        : scrollTop + scrollAmount;
    if (newPosition < 0) newPosition = 0;
    if (newPosition > maxScrollTop) newPosition = maxScrollTop;
    setScrollPosition({
      ...scrollPosition,
      scrollTop: newPosition,
      maxScrollTop
    });
    cdpContainerRef.current.scrollTop = newPosition;
  };

  const { show } = useModal();

  const getBgColor = (active, account) =>
    active && account
      ? '#f6f8f9'
      : !active && account
      ? 'blueGrayDarker'
      : active && !account
      ? 'white'
      : 'grey.200';

  return listOpen && userActiveVaults ? (
    <Fragment>
      {/* {userActiveVaults.length >= 4 && !mobile && (
        <DirectionalButton
          onClick={() => onDirectionalClick('up')}
          show={scrollTop > 0}
          bg={account ? 'blueGrayDarker' : 'white'}
        >
          <NavUp />
        </DirectionalButton>
      )} */}
      <Box
        bg={account ? 'blueGrayDarker' : 'grey.200'}
        height="100%"
        // width={mobile ? '100vw' : `${getMeasurement('navbarWidth')}px`}
        px={mobile ? '15px' : '5px'}
        pb={mobile && '15px'}
        style={{
          // borderRight: '1px solid #BEBEBE',
          // paddingLeft: '0px',
          // paddingRight: '0px',
          padding: '0'
        }}
      >
        <CdpContainer
          onScroll={debounced}
          ref={cdpContainerRef}
          cdpsLength={userActiveVaults.length}
          mobile={mobile}
          pb="5px"
        >
          {/* 概览按钮 */}
          <NavbarItem
            // baker样式
            className={active ? 'baker-overview-btn baker-overview-btn-active' : 'baker-overview-btn'}
            style={{ margin: '0 auto' }}
            key={userActiveVaults.length * 10}
            href={overviewPath + currentQuery}
            mx={mobile && '7px'}
            mt={mobile ? '15px' : '5px'}
            // bg={getBgColor(active, account)}
            bg="#f7f7f7"
            height={`${getMeasurement('navbarItemHeight')}px`}
            width={`${getMeasurement('navbarItemWidth')}px`}
            trackBtnClick={() => trackBtnClick('SelectOverview')}
          >
            <Flex
              // bg={'grey.400'}
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              py="s"
            >
              <img
                width="25px"
                height="25px"
                style={{ 'margin-bottom': '6px' }}
                src={HakerDaoLogo}
              />
              <Text
                t="p6"
                // fontWeight="bold"
                color={account ? 'black' : 'darkPurple'}
                // color={account ? 'white' : 'darkPurple'}
              >
                {lang.overview}
              </Text>
            </Flex>
          </NavbarItem>
          {userActiveVaults.map(
            ({ id, liquidationRatio, collateralizationRatio, vaultType }) => {
              console.log(vaultType, 'eth');
              const linkPath = `${navigation.basename}/${id}`;
              const active = currentPath === linkPath;
              console.log(getBgColor(active, account), 'bgcolor');
              return (
                <NavbarItem
                  className="baker-item-btn"
                  key={id}
                  href={linkPath + currentQuery}
                  mx={mobile && '7px'}
                  mt={mobile ? '15px' : '5px'}
                  bg={getBgColor(active, account)}
                  height={`${getMeasurement('navbarItemHeight') - 10}px`}
                  width={`${getMeasurement('navbarItemWidth')}px`}
                  trackBtnClick={() =>
                    trackBtnClick('SelectVault', {
                      collateral: vaultType,
                      id
                    })
                  }
                >
                  <Text
                    t="p6"
                    fontWeight="bold"
                    color={account ? 'black' : 'darkPurple'}
                    // color={account ? 'white' : 'darkPurple'}
                  >
                    {ETHToHT(vaultType)}
                  </Text>
                  {/* <RatioDisplay
                    fontSize="1.3rem"
                    ratio={collateralizationRatio
                      .toBigNumber()
                      .dp(2)
                      .times(100)}
                    ilkLiqRatio={liquidationRatio
                      .toBigNumber()
                      .dp(2)
                      .times(100)}
                    active={active}
                  /> */}
                </NavbarItem>
              );
            }
          )}
          {account && !emergencyShutdownActive && (
            <AddCdpButton account={account} show={show} mobile={mobile} />
          )}
        </CdpContainer>
      </Box>
      {/* {userActiveVaults.length >= 4 && !mobile && (
        <DirectionalButton
          onClick={() => onDirectionalClick('down')}
          show={scrollTop < maxScrollTop}
          // bg={account ? 'blueGrayDarker' : 'white'}
        >
          <NavDown />
        </DirectionalButton>
      )} */}
    </Fragment>
  ) : null;
});

export default CDPList;
